import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { ApiFailure, api, type AdminUser, type TrustLevel, type UserStatus } from '../api.ts';
import { ConfirmDanger } from '../ConfirmDanger.tsx';
import { formatWhen } from '../duration.ts';

/**
 * WHO IS ON THIS PROJECT, and the four things that can be done about it.
 *
 * THE PASSWORD IS SHOWN ONCE AND THE PAGE SAYS SO LOUDLY. The manager generates
 * it, hashes it before the row is written, and returns the plain text in that
 * one response — there is no endpoint that can produce it again, by design
 * (`apps/server/src/routes/admin.ts`). A box that looked like any other field
 * would be dismissed like any other field, and the next thing that happens is
 * an administrator asking where to find it.
 *
 * SO THE BOX IS MODAL-ISH AND STICKY: it stays until it is dismissed
 * deliberately, it carries a copy button, and dismissing it is worded as
 * throwing it away. It also survives a failed copy — `navigator.clipboard` is
 * unavailable on an insecure origin, and an administrator on http://localhost
 * still needs to be able to select the text.
 *
 * DESTRUCTIVE ROWS CONFIRM, THE REST DO NOT. Disabling somebody and resetting
 * their password both end sessions on somebody else's device, so both ask.
 * Enabling does not: it is the undo.
 */

const ROLES: readonly { id: 'labeler' | 'admin'; label: string; hint: string }[] = [
  { id: 'labeler', label: 'Labeler', hint: 'Judges the work they are handed, and nothing else.' },
  { id: 'admin', label: 'Admin', hint: 'Runs the platform: uploads, pipelines, people, deletes.' },
];

/** Owner-assigned; groundwork for settlement quotas. Enforced nowhere yet. */
const TRUSTS: readonly TrustLevel[] = ['low', 'medium', 'high', 'super'];

const STATUS_LABEL: Record<UserStatus, string> = {
  active: 'active',
  disabled: 'disabled',
  suspended: 'suspended',
};

/** The status pill reuses the recordings palette: `done` is the healthy green,
 *  `failed` the red that means "this one is not going anywhere". */
const STATUS_PILL: Record<UserStatus, string> = {
  active: 'pill-done',
  disabled: 'pill-failed',
  suspended: 'pill-queued',
};

type Secret = { user: AdminUser; password: string; sessionsRevoked?: number; reason: 'created' | 'reset' };

export function Users({
  token,
  /** The signed-in administrator, so their own row can hide the two actions
   *  the server refuses. Null when `/me` failed, which only costs the hint. */
  meId,
  onSignedOut,
}: {
  token: string;
  meId: string | null;
  onSignedOut: () => void;
}): JSX.Element {
  const [rows, setRows] = useState<AdminUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [secret, setSecret] = useState<Secret | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [condemned, setCondemned] = useState<AdminUser | null>(null);

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<'labeler' | 'admin'>('labeler');
  const [trust, setTrust] = useState<TrustLevel>('low');
  const [creating, setCreating] = useState(false);

  const onSignedOutRef = useRef(onSignedOut);
  onSignedOutRef.current = onSignedOut;

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        const { users } = await api.users(token, signal);
        setRows(users);
        setError(null);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
        setError(err instanceof ApiFailure ? err.message : 'Could not load the people.');
      }
    },
    [token],
  );

  useEffect(() => {
    const controller = new AbortController();
    setRows(null);
    setSecret(null);
    setNotice(null);
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  async function create(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (creating) return;
    setCreating(true);
    setNotice(null);
    setError(null);
    try {
      const created = await api.createUser(token, {
        email: email.trim(),
        displayName: displayName.trim(),
        role,
        trust,
      });
      if (created.tempPassword !== null) {
        setSecret({ user: created.user, password: created.tempPassword, reason: 'created' });
        setCopied(false);
      } else {
        setNotice(`${created.user.email} already has an account.`);
      }
      setEmail('');
      setDisplayName('');
      setRole('labeler');
      setTrust('low');
      await load();
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
      setError(err instanceof ApiFailure ? err.message : 'Could not create that account.');
    } finally {
      setCreating(false);
    }
  }

  async function act(user: AdminUser, what: 'disable' | 'enable' | 'reset'): Promise<void> {
    if (what === 'disable') {
      const ok = window.confirm(
        `Disable ${user.displayName}?\n\nThey will not be able to sign in, and every device they ` +
          'are signed in on stops working immediately. Their finished work stays exactly as it is.',
      );
      if (!ok) return;
    }
    if (what === 'reset') {
      // Your own reset keeps THIS page signed in (the server exempts the
      // session doing it); everything else about it is the same act.
      const ok = window.confirm(
        user.id === meId
          ? 'Reset YOUR OWN password?\n\nEvery other device you are signed in on is signed out ' +
              'and your current password stops working. This page stays signed in, and the new ' +
              'password is shown once — keep it.'
          : `Reset the password for ${user.displayName}?\n\nEvery device they are signed in on is ` +
              'signed out, and the password they have now stops working. You will be shown a new one ' +
              'once.',
      );
      if (!ok) return;
    }

    setBusyId(user.id);
    setError(null);
    setNotice(null);
    try {
      if (what === 'reset') {
        const result = await api.resetPassword(token, user.id);
        setSecret({
          user: result.user,
          password: result.tempPassword,
          sessionsRevoked: result.sessionsRevoked,
          reason: 'reset',
        });
        setCopied(false);
      } else {
        const result =
          what === 'disable'
            ? await api.disableUser(token, user.id)
            : await api.enableUser(token, user.id);
        setNotice(result.note);
      }
      await load();
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
      setError(err instanceof ApiFailure ? err.message : 'That did not work.');
    } finally {
      setBusyId(null);
    }
  }

  async function setTrustFor(user: AdminUser, trust: TrustLevel): Promise<void> {
    setBusyId(user.id);
    setError(null);
    setNotice(null);
    try {
      const result = await api.setTrust(token, user.id, trust);
      setNotice(result.note);
      await load();
    } catch (err) {
      if (err instanceof ApiFailure && err.status === 401) return onSignedOutRef.current();
      setError(err instanceof ApiFailure ? err.message : 'That did not work.');
    } finally {
      setBusyId(null);
    }
  }

  async function copy(text: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Insecure origin, or permission refused. The text is selectable either
      // way, so this is a missing convenience rather than a failure.
      setCopied(false);
    }
  }

  return (
    <section className="card">
      <div className="card-head">
        <h2>People</h2>
        <span className="topbar-spacer" />
        <button className="btn-link" type="button" onClick={() => void load()}>
          Refresh
        </button>
      </div>
      <p className="hint">
        Everyone with an account, whatever projects they are in. Disabling an account stops it
        signing in everywhere, at once; its finished work stays attributed and readable.
      </p>

      {error && (
        <div className="notice notice-error" role="alert">
          {error}
        </div>
      )}
      {notice && (
        <div className="notice notice-ok" role="status">
          {notice}
        </div>
      )}

      {/* ---- The one-time password ---------------------------------- */}
      {secret && (
        <div className="secret" role="alert">
          <div className="secret-head">
            <strong>
              {secret.reason === 'created'
                ? `${secret.user.displayName} can now sign in`
                : `New password for ${secret.user.displayName}`}
            </strong>
            <span className="secret-warn">Shown once. It cannot be recovered.</span>
          </div>

          <div className="secret-row">
            <code className="secret-value">{secret.password}</code>
            <button className="btn-quiet" type="button" onClick={() => void copy(secret.password)}>
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>

          <p className="hint-tight">
            Give it to {secret.user.email ?? secret.user.displayName} with their email address.
            {secret.sessionsRevoked !== undefined &&
              ` ${secret.sessionsRevoked === 0 ? 'They had no devices signed in' : `Signed out of ${secret.sessionsRevoked} ${secret.sessionsRevoked === 1 ? 'device' : 'devices'}`}.`}
          </p>

          <button className="btn-quiet" type="button" onClick={() => setSecret(null)}>
            I have saved it — hide it
          </button>
        </div>
      )}

      {/* ---- Add ---------------------------------------------------- */}
      <form className="adduser" onSubmit={create}>
        <div className="card-head">
          <h2>Add someone</h2>
        </div>
        <div className="adduser-grid">
          <label className="field">
            <span className="field-label">Email</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              autoComplete="off"
              required
              disabled={creating}
            />
          </label>
          <label className="field">
            <span className="field-label">Name</span>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Shown on their work"
              autoComplete="off"
              required
              disabled={creating}
            />
          </label>
          <label className="field">
            <span className="field-label">Role</span>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value as 'labeler' | 'admin')}
              disabled={creating}
            >
              {ROLES.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Trust</span>
            <select
              value={trust}
              onChange={(e) => setTrust(e.target.value as TrustLevel)}
              disabled={creating}
            >
              {TRUSTS.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="hint-tight" style={{ marginBottom: 12 }}>
          {ROLES.find((r) => r.id === role)?.hint} A password is generated for them and shown to
          you once.
        </p>
        <button
          className="btn-quiet"
          type="submit"
          disabled={creating || !email.trim() || !displayName.trim()}
        >
          {creating ? 'Creating…' : 'Add user'}
        </button>
      </form>

      {/* ---- The table ---------------------------------------------- */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th>Trust</th>
              <th>Status</th>
              <th>Created</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr>
                <td className="empty" colSpan={7}>
                  Loading…
                </td>
              </tr>
            )}
            {rows !== null && rows.length === 0 && (
              <tr>
                <td className="empty" colSpan={7}>
                  Nobody has a seat on this project yet.
                </td>
              </tr>
            )}
            {rows?.map((u) => (
              <tr key={u.id}>
                <td className="cell-name">
                  {u.email ?? <span className="cell-dim">no email — cannot sign in</span>}
                </td>
                <td>{u.displayName}</td>
                <td className="cell-dim">{u.role}</td>
                <td>
                  <select
                    className="trust-select"
                    value={u.trust ?? 'low'}
                    disabled={busyId !== null}
                    onChange={(e) => void setTrustFor(u, e.target.value as TrustLevel)}
                  >
                    {TRUSTS.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <span className={`pill ${STATUS_PILL[u.status]}`}>{STATUS_LABEL[u.status]}</span>
                </td>
                <td className="cell-dim num">{formatWhen(u.createdAt)}</td>
                <td>
                  {/*
                    * YOUR OWN ROW: Reset password only. Disabling yourself
                    * still locks the project's last administrator out, so the
                    * server refuses it and no button is offered. Resetting
                    * your own is allowed now — the session layer keeps the
                    * session doing it, so the page survives to show the new
                    * password ("I want to be able to reset my user").
                    */}
                  {u.id === meId ? (
                    <div className="btn-row">
                      <button
                        className="btn-link"
                        type="button"
                        onClick={() => void act(u, 'reset')}
                        disabled={busyId !== null}
                      >
                        Reset password
                      </button>
                    </div>
                  ) : (
                    <div className="btn-row">
                      {u.status === 'disabled' ? (
                        <button
                          className="btn-link"
                          type="button"
                          onClick={() => void act(u, 'enable')}
                          disabled={busyId !== null}
                        >
                          Enable
                        </button>
                      ) : (
                        <button
                          className="btn-link"
                          type="button"
                          onClick={() => void act(u, 'disable')}
                          disabled={busyId !== null}
                        >
                          Disable
                        </button>
                      )}
                      <button
                        className="btn-link"
                        type="button"
                        onClick={() => void act(u, 'reset')}
                        disabled={busyId !== null}
                      >
                        Reset password
                      </button>
                      {u.email && (
                        <button
                          className="btn-link btn-link-danger"
                          type="button"
                          onClick={() => setCondemned(u)}
                          disabled={busyId !== null}
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {condemned && condemned.email && (
        <ConfirmDanger
          title="Delete this account and its work, for ever?"
          warning={
            <>
              <strong>{condemned.displayName}</strong> ({condemned.email}) — the account, its
              sign-ins, and the work it did everywhere: recordings it produced, versions it
              authored, every answer it gave. All gone, for everyone, permanently. There is no
              undo and no trash.
            </>
          }
          phrase="delete"
          actionLabel="Delete the account for ever"
          busyLabel="Deleting…"
          onRun={() => api.deleteUser(token, condemned.id, 'delete').then(() => undefined)}
          onDone={(done) => {
            setCondemned(null);
            if (done) {
              setNotice('The account and its work are gone.');
              void load();
            }
          }}
        />
      )}
    </section>
  );
}
